import type { Task } from '@medplum/fhirtypes';
import { format } from 'date-fns';
import { getDueDate, getScheduledWhen, getTaskCategory, getTaskClosedAt } from './taskUtils';
import type { TaskCategoryCode } from './types';

/**
 * Render-agnostic Day-view grouping, sorting, and visibility logic over FHIR Tasks.
 * Shared by patient-web (`@liza/ui`) and patient-native so the two clients bucket and
 * order tasks identically. Anything that renders (translated labels, icons) stays in the
 * per-renderer layer.
 */

const ISO_DATE = 'yyyy-MM-dd';

export const GROUP_ORDER = ['top', 'morning', 'midDay', 'evening', 'night'] as const;
export type GroupLabel = (typeof GROUP_ORDER)[number];

/** Map a FHIR when-code or HH:MM time to a Day-view bucket. Falls back to `top`. */
export function getTimeOfDayGroup(scheduledWhen: string | null | undefined): GroupLabel {
  if (!scheduledWhen) return 'top';
  if (/^\d{2}:\d{2}$/.test(scheduledWhen)) {
    const hour = parseInt(scheduledWhen.split(':')[0]!, 10);
    if (hour >= 6 && hour < 12) return 'morning';
    if (hour >= 12 && hour < 17) return 'midDay';
    if (hour >= 17 && hour < 21) return 'evening';
    return 'night';
  }
  if (scheduledWhen.startsWith('MORN')) return 'morning';
  if (scheduledWhen.startsWith('AFT')) return 'midDay';
  if (scheduledWhen.startsWith('EVE')) return 'evening';
  if (scheduledWhen === 'NIGHT') return 'night';
  return 'top';
}

/** Bucket for a recurring occurrence. Driven by time-of-day, not period unit. */
export function getOccurrenceGroup(task: Task): GroupLabel {
  return getTimeOfDayGroup(getScheduledWhen(task));
}

/** Sort order for FHIR when codes, expressed as approximate minutes since midnight. */
export const WHEN_SORT_ORDER: Record<string, number> = {
  'MORN.early': 300,
  MORN: 540,
  'MORN.late': 630,
  'AFT.early': 720,
  AFT: 900,
  'AFT.late': 960,
  'EVE.early': 1020,
  EVE: 1140,
  'EVE.late': 1170,
  NIGHT: 1260,
};

/**
 * Converts an HH:MM string to minutes since midnight (e.g., "07:30" → 450).
 * Returns undefined if the input is not in HH:MM format.
 */
export function timeToMinutes(time: string): number | undefined {
  if (!/^\d{2}:\d{2}$/.test(time)) return undefined;
  const [h, m] = time.split(':').map(Number);
  return h! * 60 + m!;
}

/**
 * Returns a numeric sort key for a scheduledWhen value (HH:MM string or FHIR when code).
 * Used to order tasks chronologically within a time-of-day group.
 */
export function getTimeSortKey(scheduledWhen: string | null | undefined, fallback = 0): number {
  if (!scheduledWhen) return fallback;
  const minutes = timeToMinutes(scheduledWhen);
  if (minutes !== undefined) return minutes;
  return WHEN_SORT_ORDER[scheduledWhen] ?? fallback;
}

/**
 * Formats an HH:MM time string as a display string (e.g., "07:00" → "07:00 AM").
 * Returns undefined if the input is null/undefined or not in HH:MM format.
 */
export function formatTimePrefix(scheduledWhen: string | null | undefined): string | undefined {
  if (!scheduledWhen) return undefined;
  if (!/^\d{2}:\d{2}$/.test(scheduledWhen)) return undefined;
  const [hours, minutes] = scheduledWhen.split(':').map(Number);
  const h = hours! % 12 || 12;
  const ampm = hours! < 12 ? 'AM' : 'PM';
  return minutes === 0 ? `${h}:00 ${ampm}` : `${h}:${minutes!.toString().padStart(2, '0')} ${ampm}`;
}

const CATEGORY_SORT_KEY: Record<TaskCategoryCode, number> = {
  measurement: 0,
  medication: 1,
  movement: 2,
  food: 2,
  sleep: 2,
  mind: 2,
  other: 2,
  clinical: 3,
};

/** Within-bucket order: Results → Medications → Lifestyle → Tasks. */
export function getCategorySortKey(task: Task): number {
  const category = getTaskCategory(task);
  if (!category) return 3;
  return CATEGORY_SORT_KEY[category];
}

/** Today as a YYYY-MM-DD string in local time. */
function getTodayIsoDate(): string {
  return format(new Date(), ISO_DATE);
}

/** YYYY-MM-DD date a task was completed on, from {@link getTaskClosedAt}. */
export function getCompletionDate(task: {
  executionPeriod?: { end?: string };
  meta?: { lastUpdated?: string };
}): string {
  const completedAt = getTaskClosedAt(task);
  if (!completedAt) return 'Unknown';
  return format(new Date(completedAt), ISO_DATE);
}

/**
 * One-time task visibility on `selectedDate`.
 *
 * - Open tasks with a `dueDate` only appear on or after their due date — a task
 *   due Dec 1 doesn't surface in `top` when the user is viewing today.
 * - Open tasks without a `dueDate` only appear today and onward; navigating
 *   back to a past date doesn't surface them, since they weren't actionable then.
 * - Completed tasks remain visible on the day they were completed and disappear
 *   the day after.
 */
export function isOneOffVisibleOnDate(task: Task, selectedDate: string): boolean {
  if (task.status === 'completed') return getCompletionDate(task) === selectedDate;
  const dueDate = getDueDate(task);
  if (dueDate) return dueDate <= selectedDate;
  return selectedDate >= getTodayIsoDate();
}
